// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { useGetDelegatedStake, useFormatCoin } from '@mysten/core';
import { useCurrentAccount } from '@mysten/dapp-kit';
import { Heading, Text } from '@mysten/ui';
import { Link } from 'react-router-dom';

import { StakeModal } from '~/components/StakeModal';
import { SelectValidatorModal } from '~/components/SelectValidator';
import { useCpcStakeFlow } from '~/hooks/useCpcStakeFlow';
import { CPC_SYMBOL, DGC_TYPE_ARG } from '~/utils/cpcCoin';

export function TotalStaked({ address }: { address: string }) {
	const account = useCurrentAccount();
	const isOwnAddress =
		account?.address?.toLowerCase() === address.toLowerCase();
	const flow = useCpcStakeFlow();
	const { data: delegatedStake } = useGetDelegatedStake({ address });

	const totalActivePendingStake =
		delegatedStake?.reduce(
			(acc, curr) =>
				curr.stakes.reduce((total, { principal }) => total + BigInt(principal), acc),
			0n,
		) ?? 0n;

	const [formatted] = useFormatCoin(totalActivePendingStake, DGC_TYPE_ARG);

	return (
		<div className="flex flex-wrap items-center gap-3">
			<div className="flex min-w-44 items-center gap-3 rounded-xl bg-white/10 border border-white/20 px-4 py-3 backdrop-blur-sm">
				<div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#38BDF8]/20 text-[#38BDF8] text-xs font-bold">
					{CPC_SYMBOL.slice(0, 1)}
				</div>
				<div className="flex flex-col">
					<Text variant="pBody/semibold" color="dark" uppercase>
						已质押
					</Text>
					<Heading variant="heading6/semibold" color="blue" as="div">
						{formatted} {CPC_SYMBOL}
					</Heading>
				</div>
				{isOwnAddress ? (
					<button
						type="button"
						onClick={flow.openStakeModal}
						className="ml-auto bg-[#38BDF8] text-white rounded-lg px-2.5 py-1.5 text-xs font-semibold hover:bg-[#38BDF8]/90"
					>
						质押
					</button>
				) : null}
			</div>
			<Link to="/stake" className="text-xs text-[#38BDF8] hover:underline">
				质押中心
			</Link>

			{isOwnAddress ? (
				<>
					<StakeModal
						isOpen={flow.isStakeOpen}
						onClose={() => flow.setIsStakeOpen(false)}
						balance={flow.availableCpc}
						stakeableBalance={flow.stakeableCpc}
						symbol={CPC_SYMBOL}
						onConfirm={(amount, addrs) =>
							flow.handleStakeModalConfirm(
								amount,
								addrs.map((a) => ({ address: a })),
							)
						}
					/>
					<SelectValidatorModal
						isOpen={flow.isValidatorOpen}
						validators={flow.validatorOptions}
						onClose={() => flow.setIsValidatorOpen(false)}
						onSelect={flow.executeStake}
					/>
				</>
			) : null}
		</div>
	);
}
